import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Check, Star, Sparkles, ShoppingBag } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getAllSubscriptionPlans } from "../../../../api/action/subscription.jsx";
import { getIcon } from "../utils/iconResolver";
import data from "../data/retailData.json";
import { mergePricingPlansWithApi } from "../../shared/pricingUtils";
import { academyLogger } from "utils/logger";

const { palette: P, pricing } = data;

const RetailPricing = () => {
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [plans, setPlans] = useState([]);
  const [pricingUnavailable, setPricingUnavailable] = useState(false);
  const dispatch = useDispatch();
  const nav = useNavigate();

  useEffect(() => {
    let isMounted = true;

    const loadPlans = async () => {
      try {
        const apiPlans = await dispatch(getAllSubscriptionPlans({
          businessDomain: "RETAIL"
        }));

        if (!isMounted) {
          return;
        }

        const mappedPlans = mergePricingPlansWithApi({
          apiPlans,
          fallbackPlans: pricing.plans,
          businessDomain: "RETAIL",
          tierOrder: ["STARTER", "GROWTH", "AUTHORITY"],
        });
        setPlans(mappedPlans);
        setPricingUnavailable(mappedPlans.length === 0);
      } catch (error) {
        academyLogger.error("Failed to load retail pricing plans:", error);
        setPlans([]);
        setPricingUnavailable(true);
      }
    };

    loadPlans();

    return () => {
      isMounted = false;
    };
  }, [dispatch]);

  return (
    <section id="retail-pricing" style={{ background: P.white, padding: "clamp(60px, 8vw, 96px) clamp(20px, 4vw, 40px)" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>

        <div style={{ textAlign: "center", marginBottom: "clamp(36px, 5vw, 56px)" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(93,162,162,0.10)", border: "1px solid rgba(93,162,162,0.25)", padding: "8px 18px", borderRadius: 100, marginBottom: 20 }}>
            <Sparkles style={{ width: 14, height: 14, color: P.teal }} />
            <span style={{ color: P.teal, fontSize: 13, fontWeight: 700, letterSpacing: "0.05em" }}>{pricing.badge}</span>
          </div>
          <h2 style={{ fontSize: "clamp(32px, 5vw, 48px)", fontWeight: 900, letterSpacing: "-0.02em", color: P.ink, lineHeight: 1.1, marginBottom: 12 }}>{pricing.headline}</h2>
          <p style={{ fontSize: "clamp(15px, 2vw, 18px)", color: P.muted, marginBottom: 32 }}>{pricing.subheadline}</p>

          {/* Billing toggle */}
          <div style={{ display: "inline-flex", alignItems: "center", background: P.bgWarm, borderRadius: 100, padding: 4, border: `1px solid ${P.border}` }}>
            {["monthly", "annual"].map(cycle => (
              <button key={cycle} onClick={() => setBillingCycle(cycle)}
                style={{ padding: "clamp(7px, 1.5vw, 9px) clamp(16px, 2.5vw, 24px)", borderRadius: 100, fontSize: "clamp(12px, 1.5vw, 14px)", fontWeight: 600, border: "none", cursor: "pointer", transition: "all 0.22s", background: billingCycle === cycle ? P.teal : "transparent", color: billingCycle === cycle ? "white" : P.muted, display: "flex", alignItems: "center", gap: 8, boxShadow: billingCycle === cycle ? "0 4px 14px rgba(93,162,162,0.30)" : "none" }}>
                {cycle === "monthly" ? "Monthly" : "Annual"}
                {cycle === "annual" && <span style={{ background: "#d1fae5", color: "#065f46", padding: "2px 8px", borderRadius: 100, fontSize: 11, fontWeight: 700 }}>-20%</span>}
              </button>
            ))}
          </div>
        </div>

        {/* Plans */}
        {!pricingUnavailable && plans.length > 0 ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))", gap: 22, alignItems: "center" }}>
          {plans.map((plan, i) => (
            <div key={i} style={{ position: "relative", transform: plan.highlight ? "scale(1.03)" : "scale(1)" }}>
              {plan.popularBadge && (
                <div style={{ position: "absolute", top: -14, left: "50%", transform: "translateX(-50%)", background: P.teal, color: "white", padding: "6px 18px", borderRadius: 100, fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 6, boxShadow: "0 6px 18px rgba(93,162,162,0.40)", zIndex: 1 }}>
                  <Star style={{ width: 11, height: 11, fill: "white" }} /> {plan.popularBadge}
                </div>
              )}
              <div style={{ background: plan.highlight ? P.darkBg : P.white, border: plan.highlight ? `2px solid ${P.teal}` : `1.5px solid ${P.border}`, borderRadius: 22, padding: "clamp(28px, 4vw, 38px) clamp(22px, 3vw, 30px)", height: "100%", display: "flex", flexDirection: "column", boxShadow: plan.highlight ? "0 20px 64px rgba(0,0,0,0.20)" : "0 2px 14px rgba(0,0,0,0.04)" }}>

                <div style={{ marginBottom: 24 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "center", background: plan.highlight ? "rgba(93,162,162,0.16)" : P.bgWarm, color: plan.highlight ? P.tealLight : P.teal, border: plan.highlight ? "1px solid rgba(93,162,162,0.26)" : `1px solid ${P.border}` }}>
                    {getIcon(plan.icon, { style: { width: 20, height: 20 } })}
                  </div>
                  <h3 style={{ fontSize: "clamp(18px, 2.5vw, 22px)", fontWeight: 800, color: plan.highlight ? "white" : P.ink }}>{plan.name}</h3>
                  <p style={{ color: plan.highlight ? "rgba(255,255,255,0.46)" : P.muted, fontSize: "clamp(12px, 1.4vw, 14px)", marginTop: 4 }}>{plan.subtitle}</p>
                </div>

                <div style={{ marginBottom: 24 }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
                    <span style={{ fontSize: "clamp(36px, 5vw, 48px)", fontWeight: 900, color: plan.highlight ? "white" : P.ink, letterSpacing: "-0.02em" }}>
                      {billingCycle === "monthly" ? plan.price : plan.yearlyPrice}
                    </span>
                    <span style={{ color: plan.highlight ? "rgba(255,255,255,0.42)" : P.muted, fontSize: "clamp(13px, 1.5vw, 16px)" }}>/{billingCycle === "monthly" ? "mo" : "yr"}</span>
                  </div>
                  {billingCycle === "annual" && <p style={{ color: "#4ade80", fontSize: 13, fontWeight: 600, marginTop: 4 }}>Save 20% with annual billing</p>}
                  {plan.setupFeeText && <p style={{ color: plan.highlight ? "rgba(255,255,255,0.6)" : P.muted, fontSize: 13, marginTop: 4 }}>+ {plan.setupFeeText}</p>}
                </div>

                <div style={{ height: 1, background: plan.highlight ? "rgba(93,162,162,0.18)" : P.border, marginBottom: 22 }} />

                <div style={{ flex: 1, marginBottom: 28 }}>
                  {plan.features.map((f, fi) => (
                    <div key={fi} style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 12 }}>
                      <div style={{ width: 20, height: 20, background: plan.highlight ? "rgba(93,162,162,0.16)" : "rgba(93,162,162,0.09)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
                        <Check style={{ width: 12, height: 12, color: plan.highlight ? P.tealLight : P.teal }} />
                      </div>
                      <span style={{ color: plan.highlight ? "rgba(255,255,255,0.78)" : P.ink2, fontSize: "clamp(12px, 1.4vw, 14px)", lineHeight: 1.5 }}>{f}</span>
                    </div>
                  ))}
                </div>

                <button onClick={() => nav("/contact")}
                  style={{ width: "100%", padding: "clamp(11px, 1.5vw, 13px) 0", borderRadius: 11, fontSize: "clamp(13px, 1.5vw, 15px)", fontWeight: 700, border: "none", cursor: "pointer", transition: "all 0.22s", background: plan.highlight ? P.teal : P.bgWarm, color: plan.highlight ? "white" : P.ink2, boxShadow: plan.highlight ? "0 6px 22px rgba(93,162,162,0.38)" : "none" }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.background = plan.highlight ? P.tealDark : "#f5e8d8"; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.background = plan.highlight ? P.teal : P.bgWarm; }}>
                  {plan.ctaLabel}
                </button>
              </div>
            </div>
          ))}
        </div>
        ) : (
          <div style={{ textAlign: "center", background: P.white, border: `1px solid ${P.border}`, borderRadius: 22, padding: "36px 24px" }}>
            <h3 style={{ fontSize: "clamp(22px, 3vw, 28px)", fontWeight: 800, color: P.ink, marginBottom: 10 }}>Pricing is currently unavailable</h3>
            <p style={{ color: P.muted, fontSize: 15, lineHeight: 1.7, maxWidth: 560, margin: "0 auto 20px" }}>
              We could not load the latest Retail subscription pricing right now. Please contact our team for current plan details.
            </p>
            <button
              onClick={() => nav("/contact")}
              style={{ background: P.teal, color: "white", border: "none", padding: "12px 28px", borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: "pointer" }}
            >
              Talk to Sales
            </button>
          </div>
        )}

        {/* Enterprise banner */}
        <div style={{ background: P.darkBg, borderRadius: 24, padding: "clamp(40px, 6vw, 58px) clamp(24px, 5vw, 48px)", marginTop: 48, textAlign: "center", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle at 1.5px 1.5px, rgba(255,230,190,0.04) 1px, transparent 0)", backgroundSize: "40px 40px" }} />
          <div style={{ position: "absolute", top: -90, right: -90, width: 340, height: 340, borderRadius: "50%", background: "radial-gradient(circle, rgba(93,162,162,0.15) 0%, transparent 70%)" }} />
          <div style={{ position: "relative" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(93,162,162,0.14)", border: "1px solid rgba(93,162,162,0.26)", color: P.tealLight, padding: "7px 18px", borderRadius: 100, fontSize: 12, fontWeight: 700, marginBottom: 20, letterSpacing: "0.06em" }}>
              <ShoppingBag style={{ width: 13, height: 13 }} /> {pricing.enterprise.badge}
            </div>
            <h3 style={{ fontSize: "clamp(24px, 4vw, 36px)", fontWeight: 900, color: "white", marginBottom: 14, letterSpacing: "-0.02em" }}>{pricing.enterprise.headline}</h3>
            <p style={{ color: "rgba(255,255,255,0.46)", fontSize: "clamp(14px, 2vw, 17px)", maxWidth: 500, margin: "0 auto 34px", lineHeight: 1.75 }}>{pricing.enterprise.description}</p>
            <button onClick={() => nav("/contact")}
              style={{ background: P.teal, color: "white", border: "none", padding: "clamp(12px, 2vw, 15px) clamp(28px, 4vw, 40px)", borderRadius: 12, fontSize: "clamp(13px, 1.5vw, 15px)", fontWeight: 700, cursor: "pointer", boxShadow: "0 8px 28px rgba(93,162,162,0.45)", transition: "all 0.25s" }}
              onMouseEnter={e => { e.currentTarget.style.background = P.tealDark; e.currentTarget.style.transform = "translateY(-2px)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = P.teal; e.currentTarget.style.transform = "none"; }}>
              {pricing.enterprise.button}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RetailPricing;
